/**
 * useMissionCalculator Hook
 *
 * Real-time mission statistics calculation
 * Automatically recalculates when mission changes
 */

import { useMemo } from 'react';
import { useMissionStore } from '@/stores/useMissionStore';
import type { MissionStatistics } from '../types/mission.types';
import { calculateMissionStatistics } from '../utils/mission-calculator';

/**
 * Hook return type
 */
interface UseMissionCalculatorReturn {
  statistics: MissionStatistics;
  isCalculating: boolean;
}

/**
 * Mission statistics calculator hook
 *
 * Automatically recalculates statistics when waypoints change
 */
export function useMissionCalculator(): UseMissionCalculatorReturn {
  const mission = useMissionStore((state) => state.currentMission);

  // Calculate statistics (memoized to prevent unnecessary recalculations)
  const statistics = useMemo<MissionStatistics>(() => {
    if (!mission) {
      return {
        waypointCount: 0,
        totalDistance: 0,
        horizontalDistance: 0,
        verticalDistance: 0,
        estimatedFlightTime: 0,
        estimatedBatteryUsage: 0,
        maxAltitude: 0,
        minAltitude: 0,
        averageAltitude: 0,
        averageSpeed: 0,
      };
    }

    return calculateMissionStatistics(mission);
  }, [mission]);

  // Since calculation is synchronous and memoized, we're never "calculating"
  // This is here for future async calculations if needed
  const isCalculating = false;

  return {
    statistics,
    isCalculating,
  };
}
